import { Link } from "react-router-dom";
import { MdHandshake, MdPerson } from "react-icons/md";
import { useSubscriptionPathsSummary } from "hooks/useSubscriptionPathsSummary";
import SlaveAccountRowStatus from "components/client/SlaveAccountRowStatus";

/** Set to false to re-enable Path 2 UI on the subscription overview. */
const PATH2_TEMPORARILY_DISABLED = true;

const PATH_META = {
  path1: {
    title: "Path 1 — Broker partner",
    subtitle: "FXPro partner flow · promo WLCMBP",
    icon: MdHandshake,
    manageTo: "/client/subscriptions/Path1BrokerPartner",
    accent:
      "border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-500/30 dark:bg-emerald-500/5",
    badge: "text-emerald-700 dark:text-emerald-300",
  },
  path2: {
    title: "Path 2 — Personal broker",
    subtitle: "Your own broker · promo WLCMPP",
    icon: MdPerson,
    manageTo: "/client/subscriptions/Path2PersonnalBroker",
    accent:
      "border-sky-200/80 bg-sky-50/50 dark:border-sky-500/30 dark:bg-sky-500/5",
    badge: "text-sky-700 dark:text-sky-300",
  },
};

function PathSummaryCard({
  pathKey,
  accounts,
  loading,
  error,
  paidSlotsCount,
  awaitingApprovalCount = 0,
  partnerStatus,
  onRetry,
  temporarilyDisabled = false,
}) {
  const meta = PATH_META[pathKey];
  const Icon = meta.icon;
  const connected = accounts.filter((a) => a.brokerConnected).length;
  const linked = accounts.filter((a) => a.linkedToCopier).length;
  const rateLimited = /request limit/i.test(String(error || ""));

  return (
    <section
      className={`flex flex-col rounded-[20px] border-2 p-5 shadow-sm dark:bg-navy-800/60 ${meta.accent}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm dark:bg-navy-900 ${meta.badge}`}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-navy-800 dark:text-white">
              {meta.title}
            </h3>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
              {meta.subtitle}
            </p>
          </div>
        </div>
        {!temporarilyDisabled ? (
          <Link
            to={meta.manageTo}
            className="shrink-0 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-300"
          >
            Manage
          </Link>
        ) : null}
      </div>

      {temporarilyDisabled ? (
        <p
          className="mx-auto mt-2 block w-fit rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-center text-sm font-medium text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
          role="status"
        >
          Path 2 is temporarily disabled.
        </p>
      ) : (
        <>
      {awaitingApprovalCount > 0 ? (
        <p
          className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
          role="status"
        >
          {awaitingApprovalCount} slot{awaitingApprovalCount > 1 ? "s" : ""}{" "}
          <strong>Waiting for Admin Approval</strong>
        </p>
      ) : null}
      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-white/60 bg-white/80 px-3 py-2 dark:border-white/10 dark:bg-navy-900/50">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Paid slots
          </dt>
          <dd className="mt-0.5 text-lg font-bold tabular-nums text-navy-800 dark:text-white">
            {paidSlotsCount}
          </dd>
        </div>
        <div className="rounded-xl border border-white/60 bg-white/80 px-3 py-2 dark:border-white/10 dark:bg-navy-900/50">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Accounts
          </dt>
          <dd className="mt-0.5 text-lg font-bold tabular-nums text-navy-800 dark:text-white">
            {accounts.length}
          </dd>
        </div>
        <div className="rounded-xl border border-white/60 bg-white/80 px-3 py-2 dark:border-white/10 dark:bg-navy-900/50">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Linked
          </dt>
          <dd className="mt-0.5 text-lg font-bold tabular-nums text-navy-800 dark:text-white">
            {linked}
          </dd>
        </div>
        <div className="rounded-xl border border-white/60 bg-white/80 px-3 py-2 dark:border-white/10 dark:bg-navy-900/50">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Connected
          </dt>
          <dd className="mt-0.5 text-lg font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
            {connected}
          </dd>
        </div>
      </dl>

      {pathKey === "path1" && partnerStatus ? (
        <p className="mt-3 text-xs font-medium text-gray-600 dark:text-gray-400">
          Partner e-mail:{" "}
          <span className={meta.badge}>
            {partnerStatus === "approved"
              ? "Verified"
              : partnerStatus === "pending"
                ? "Pending approval"
                : partnerStatus}
          </span>
        </p>
      ) : null}

      <div className="mt-4 min-h-[4rem] flex-1">
        {loading && accounts.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : error ? (
          <div className="space-y-2">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            {rateLimited ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The service is busy — wait about one minute, then retry.
              </p>
            ) : null}
            <button
              type="button"
              onClick={onRetry}
              className="text-xs font-semibold text-brand-600 hover:underline dark:text-brand-300"
            >
              Retry
            </button>
          </div>
        ) : accounts.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No trading account on this path yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {accounts.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200/80 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-900/40"
              >
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span
                    className={`rounded-md px-2 py-0.5 font-mono text-xs font-bold ${meta.badge} bg-white dark:bg-navy-800`}
                  >
                    {row.copierDisplayName || `S${row.slot}`}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {row.accountType} · {row.accountNumber || "—"}
                  </span>
                </div>
                <SlaveAccountRowStatus row={row} statusLoadingId={null} />
              </li>
            ))}
          </ul>
        )}
      </div>
        </>
      )}

    </section>
  );
}

export default function SubscriptionPathsSummary({ session }) {
  const {
    path1Accounts,
    path2Accounts,
    loading,
    error,
    refresh,
    paidSlotsPath1,
    paidSlotsPath2,
    awaitingApprovalPath1,
    awaitingApprovalPath2,
    partnerStatus,
  } = useSubscriptionPathsSummary(session);

  return (
    <div className="space-y-4">
      {error ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
          {error}
          {/request limit/i.test(error) ? (
            <span className="block mt-1 text-xs opacity-90">
              Please wait a moment, then click Retry on a path card.
            </span>
          ) : null}
        </p>
      ) : null}
      <div className="grid gap-6 lg:grid-cols-2">
        <PathSummaryCard
          pathKey="path1"
          accounts={path1Accounts}
          loading={loading}
          error={error}
          paidSlotsCount={paidSlotsPath1}
          awaitingApprovalCount={awaitingApprovalPath1}
          partnerStatus={partnerStatus}
          onRetry={() => void refresh()}
        />
        <PathSummaryCard
          pathKey="path2"
          accounts={path2Accounts}
          loading={loading}
          error={error}
          paidSlotsCount={paidSlotsPath2}
          awaitingApprovalCount={awaitingApprovalPath2}
          onRetry={() => void refresh()}
          temporarilyDisabled={PATH2_TEMPORARILY_DISABLED}
        />
      </div>
    </div>
  );
}
